import {
  Component,
  effect,
  inject,
  signal,
  model,
  output,
  untracked,
  computed,
  input,
} from '@angular/core';
import { FicheConsultantHeaderComponent } from './fiche-consultant-header/fiche-consultant-header.component';
import { ConsultantModel } from '@lib/models/consultant/consultant.model';
import { OuiTabsModule } from 'omnium-ui/tabs';
import { FicheConsultantProfilComponent } from './tabs/fiche-consultant-profil/fiche-consultant-profil.component';
import { FicheConsultantHabilitationsComponent } from './tabs/fiche-consultant-habilitations/fiche-consultant-habilitations.component';
import { FicheConsultantDocumentsComponent } from './tabs/fiche-consultant-documents/fiche-consultant-documents.component';
import { OuiLoading } from 'omnium-ui/loading';
import { ConsultantService } from '@lib/services/consultant/consultant.service';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { ConsultantDocument } from '@lib/models/consultant/consultant-document.model';
import { ResiliationModel } from '@lib/models/resiliation/resiliation.model';
import { DocumentService } from '@lib/services/documents/documents.service';
import { OuiSearchbar } from 'omnium-ui/searchbar';
import { FormControl } from '@angular/forms';
import { OuiButton } from 'omnium-ui/button';
import { OuiIconModule } from 'omnium-ui/icon';
import { FicheConsultantArchiveComponent } from './tabs/fiche-consultant-profil-archive/fiche-consultant-archive.component';
import { debounceTime } from 'rxjs';
import { DocumentsConsultantService } from '@lib/services/documents/documents-consultant.service';
import { OuiMenu } from 'omnium-ui/menu';
import { MenuNode } from 'omnium-ui/shared';
import { AuthService } from '@lib/auth/auth.service';
import { DemandePjPanelComponent } from '../demande-pj/demande-pj-panel.component';
import { FicheConsultantActiviteComponent } from './tabs/fiche-consultant-activite/fiche-consultant-activite.component';

const FICHE_CONSULTANT_TAB_INDEX = {
  profile: 0,
  habilitations: 1,
  documents: 2,
  activity: 3,
} as const;

@Component({
  selector: 'lib-fiche-consultant',
  standalone: true,
  imports: [
    FicheConsultantHeaderComponent,
    FicheConsultantProfilComponent,
    FicheConsultantHabilitationsComponent,
    FicheConsultantDocumentsComponent,
    FicheConsultantArchiveComponent,
    OuiTabsModule,
    OuiLoading,
    OuiSearchbar,
    OuiButton,
    OuiIconModule,
    OuiMenu,
    DemandePjPanelComponent,
    FicheConsultantActiviteComponent,
  ],
  templateUrl: './fiche-consultant.component.html',
  styleUrl: './fiche-consultant.component.scss',
})
export class FicheConsultantComponent {
  private readonly consultantService = inject(ConsultantService);
  private readonly documentsService = inject(DocumentService);
  private readonly documentsConsultantService = inject(DocumentsConsultantService);
  private readonly snackbarService = inject(OuiSnackbarService);

  private readonly authService = inject(AuthService);

  readonly codeConsultant = model.required<string>();
  readonly refreshDocs = input.required<number>();
  readonly enableNavigation = input<boolean>(false);
  readonly disableResiliation = input<boolean>(false);
  readonly viewerDocument = input<ConsultantDocument | undefined>(undefined);
  readonly isExpendSideNav = input<boolean>();

  readonly documentOpen = output<{
    document: ConsultantDocument;
    isUploadDoc: boolean;
  }>();
  readonly requestResiliation = output();
  readonly close = output();
  readonly expendSideNav = output();
  readonly onRefreshDocs = output();

  readonly consultant = signal<ConsultantModel | null>(null);
  readonly consultantResiliation = signal<ResiliationModel | null>(null);
  readonly documents = signal<ConsultantDocument[]>([]);
  readonly documentsFiltered = signal<ConsultantDocument[] | null>(null);
  readonly isArchive = signal<boolean>(false);
  readonly archiveRole = signal<number>(1);
  readonly displayDemandePjPanel = signal<boolean>(false);
  readonly isBo = this.authService.isBo();

  readonly searchControl = new FormControl<string>('');
  readonly searchValue = signal<string>('');
  readonly selectedTabIndex = signal<number>(FICHE_CONSULTANT_TAB_INDEX.profile);
  readonly ficheConsultantTabIndex = FICHE_CONSULTANT_TAB_INDEX;

  private readonly history = signal<ConsultantModel[]>([]);

  readonly showBackButton = computed<boolean>(() => this.history().length > 1);

  archiveMenuItems: MenuNode[] = [
    {
      name: 'SHM',
      action: () => {
        this.archiveRole.set(1);
        this.setIsArchive();
      },
    },
    {
      name: 'GCOM',
      action: () => {
        this.archiveRole.set(2);
        this.setIsArchive();
      },
    },
  ];

  constructor() {
    this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe(val => {
      if (val !== null) {
        const valLower = val.trim().toLocaleLowerCase();

        if (this.isArchive()) {
          this.searchValue.set(valLower);
        } else {
          this.documentsFiltered.set(
            this.documents().filter(
              doc =>
                doc.typeDocument.toLocaleLowerCase().includes(valLower) ||
                doc.fileName.toLocaleLowerCase().includes(valLower)
            )
          );
        }
      }
    });

    effect(() => {
      if (this.codeConsultant()) {
        this.getConsultant();
      }
      if (this.refreshDocs() && this.codeConsultant() && this.isBo) {
        this.getDocuments(this.codeConsultant());
      }
    });
  }

  private async getConsultant() {
    const codeConsultant = this.codeConsultant();

    if (untracked(this.consultant)?.code === codeConsultant) return;

    this.consultant.set(null);

    try {
      const consultantFiche =
        await this.consultantService.getFicheConsultant(codeConsultant);

      const consultantResiliation =
        consultantFiche.resiliation ??
        ({
          codeConsultant: consultantFiche.consultant.code,
          dateResiliation: consultantFiche.consultant.dateResiliation,
          motif: consultantFiche.consultant.motifResiliation,
        } as ResiliationModel);

      this.consultant.set(consultantFiche.consultant);
      this.consultantResiliation.set(consultantResiliation);
      this.history.set([...this.history(), consultantFiche.consultant]);
      this.documentsConsultantService.setConsultantStatutJuridique(
        consultantFiche.consultant.statutJuridiques ?? []
      );
    } catch (error) {
      this.snackbarService.open(
        'Une erreur est survenue durant la récupération des informations du consultant',
        'error'
      );
      console.error(error);
    }
  }

  async refreshConsultant(selectedTabIndex: number = this.selectedTabIndex()) {
    const codeConsultant = this.codeConsultant();

    this.selectedTabIndex.set(selectedTabIndex);
    this.consultant.set(null);

    try {
      const consultantFiche =
        await this.consultantService.getFicheConsultant(codeConsultant);

      this.consultant.set(consultantFiche.consultant);
      this.consultantResiliation.set(
        consultantFiche.resiliation ??
          ({
            codeConsultant: consultantFiche.consultant.code,
            dateResiliation: consultantFiche.consultant.dateResiliation,
            motif: consultantFiche.consultant.motifResiliation,
          } as ResiliationModel)
      );
    } catch (error) {
      this.snackbarService.open(
        'Une erreur est survenue durant le rafraîchissement des informations du consultant',
        'error'
      );
      console.error(error);
    }
  }

  private async getDocuments(codeConsultant: string) {
    try {
      this.documents.set([]);

      const documents = await this.documentsService.getDocumentsBo(codeConsultant);

      this.documents.set(documents);
      this.documentsFiltered.set(documents);
      if (this.consultant()) {
        this.consultant()!.documents = documents;
      }
    } catch (error) {
      this.snackbarService.open(
        'Une erreur est survenue durant la récupération des documents du consultant',
        'error'
      );
      console.error(error);
    }
  }

  goToPreviousConsultant() {
    const newHistory = this.history().slice(0, -1);
    this.history.set(newHistory);
    const consultant = newHistory[newHistory.length - 1];

    if (consultant) {
      this.consultant.set(consultant);
      this.codeConsultant.set(consultant.code);
      this.documents.set(consultant.documents!);
    }
  }

  resetHistory() {
    const consultant = this.history()[0];

    if (consultant) {
      this.consultant.set(consultant);
      this.history.set([consultant]);
    }
  }

  setIsArchive() {
    this.searchControl.reset('');
    this.isArchive.set(!this.isArchive());
  }

  setDisplayDemandePjPanel() {
    this.displayDemandePjPanel.set(!this.displayDemandePjPanel());
  }
}
